import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Badge,
  Text,
  Button,
  Group,
  Stack,
  Loader,
  Center,
  Select,
  TextInput,
  Anchor,
  Card,
  SimpleGrid,
  SegmentedControl,
  ActionIcon,
  Tooltip,
  Checkbox,
  Paper,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { lostClaimsApi } from '../api/lostClaims';
import { useSelection } from '../hooks/useSelection';
import { profileLink } from '../utils/profileLink';
import { CLAIM_STATUS_LABELS, CLAIM_STATUS_COLORS } from '../constants/status';
import type { LostClaim, ClaimStatus } from '../types';
import {
  pageContainer,
  listRow,
  listRowThumb,
  listRowThumbImg,
  itemCard,
  itemCardImageWrapper,
  itemCardImage,
  itemCardPlaceholder,
} from '../styles/theme.css';


function ClaimCard({
  claim,
  selected,
  onToggle,
}: {
  claim: LostClaim;
  selected: boolean;
  onToggle: (id: number) => void;
}) {
  const navigate = useNavigate();
  return (
    <Card
      className={itemCard}
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      onClick={() => navigate(`/lost-claims/${claim.id}`)}
      style={{ outline: selected ? '2px solid var(--mantine-color-blue-5)' : undefined }}
    >
      <div className={itemCardImageWrapper} style={{ position: 'relative' }}>
        {claim.cover_image ? (
          <img src={claim.cover_image} alt={claim.description} className={itemCardImage} />
        ) : (
          <div className={itemCardPlaceholder}>🔍</div>
        )}
        <div
          style={{ position: 'absolute', top: 6, left: 6 }}
          onClick={(e) => { e.stopPropagation(); onToggle(claim.id); }}
        >
          <Checkbox checked={selected} onChange={() => onToggle(claim.id)} size="sm" />
        </div>
      </div>
      <Stack p="sm" gap={4}>
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="sm" lineClamp={2} style={{ flex: 1 }}>
            {claim.description}
          </Text>
          <Badge size="xs" color={CLAIM_STATUS_COLORS[claim.status]} variant="light" tt="none">
            {CLAIM_STATUS_LABELS[claim.status]}
          </Badge>
        </Group>
        {(claim.color || claim.brand) && (
          <Text size="xs" c="dimmed">
            {[claim.color, claim.brand].filter(Boolean).join(' · ')}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          {claim.contact_name}
          {claim.lost_date
            ? ` · ${new Date(claim.lost_date + 'T12:00:00').toLocaleDateString('da-DK')}`
            : ''}
        </Text>
      </Stack>
    </Card>
  );
}

function ClaimRow({
  claim,
  selected,
  onToggle,
  onUpdate,
  updating,
}: {
  claim: LostClaim;
  selected: boolean;
  onToggle: (id: number) => void;
  onUpdate: (id: number, status: ClaimStatus) => void;
  updating: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div
      className={listRow}
      onClick={() => navigate(`/lost-claims/${claim.id}`)}
      style={{ background: selected ? 'var(--mantine-color-blue-0)' : undefined }}
    >
      <Checkbox
        checked={selected}
        onChange={() => onToggle(claim.id)}
        onClick={(e) => e.stopPropagation()}
        size="sm"
        mr="xs"
      />
      <div className={listRowThumb}>
        {claim.cover_image ? (
          <img src={claim.cover_image} alt={claim.description} className={listRowThumbImg} />
        ) : (
          '🔍'
        )}
      </div>

      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>
            {claim.description}
          </Text>
          <Badge size="xs" color={CLAIM_STATUS_COLORS[claim.status]} variant="light" tt="none">
            {CLAIM_STATUS_LABELS[claim.status]}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {[claim.color, claim.brand].filter(Boolean).join(' · ')}
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed">
            {claim.contact_name}
          </Text>
          {claim.social_profile && (
            <>
              <Text size="xs" c="dimmed">·</Text>
              {profileLink(claim.contact_method, claim.social_profile) ? (
                <Anchor
                  href={profileLink(claim.contact_method, claim.social_profile)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  {claim.social_profile}
                </Anchor>
              ) : (
                <Text size="xs" c="dimmed">{claim.social_profile}</Text>
              )}
            </>
          )}
        </Group>
      </Stack>

      <Group gap="xs" onClick={(e) => e.stopPropagation()}>
        {claim.matched_item_id && (
          <Anchor size="xs" onClick={() => navigate(`/items/${claim.matched_item_id}`)}>
            Se genstand
          </Anchor>
        )}
        {claim.status === 'open' && (
          <Button
            size="xs"
            variant="light"
            color="gray"
            onClick={() => onUpdate(claim.id, 'closed')}
            loading={updating}
          >
            Luk
          </Button>
        )}
        {claim.status === 'closed' && (
          <Button
            size="xs"
            variant="light"
            color="orange"
            onClick={() => onUpdate(claim.id, 'open')}
            loading={updating}
          >
            Genåbn
          </Button>
        )}
      </Group>
    </div>
  );
}

export function LostClaimsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | 'all'>('open');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const { selectedIds, toggleItem, toggleAll, clearSelection } = useSelection();

  const { data: claims, isLoading, error } = useQuery({
    queryKey: ['lost-claims', statusFilter],
    queryFn: () => lostClaimsApi.list({ status: statusFilter === 'all' ? undefined : statusFilter }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ClaimStatus }) =>
      lostClaimsApi.update(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lost-claims'] });
      notifications.show({ message: 'Status opdateret', color: 'green' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map((id) => lostClaimsApi.delete(id))),
    onSuccess: (_, ids) => {
      qc.invalidateQueries({ queryKey: ['lost-claims'] });
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      clearSelection();
      notifications.show({
        message: `${ids.length} efterlysning${ids.length !== 1 ? 'er' : ''} slettet`,
        color: 'green',
      });
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const displayed = useMemo(() => {
    if (!claims) return [];
    const q = search.trim().toLowerCase();
    if (!q) return claims;
    return claims.filter(
      (c) =>
        c.description.toLowerCase().includes(q) ||
        c.contact_name.toLowerCase().includes(q) ||
        (c.color ?? '').toLowerCase().includes(q) ||
        (c.brand ?? '').toLowerCase().includes(q) ||
        (c.social_profile ?? '').toLowerCase().includes(q)
    );
  }, [claims, search]);

  const allSelected = displayed.length > 0 && selectedIds.size === displayed.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className={pageContainer}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Efterlysninger</Title>
          <Group gap="xs">
            <Tooltip label="Kortvisning">
              <ActionIcon
                variant={view === 'grid' ? 'filled' : 'default'}
                onClick={() => setView('grid')}
                aria-label="Kortvisning"
              >
                ⊞
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Listevisning">
              <ActionIcon
                variant={view === 'list' ? 'filled' : 'default'}
                onClick={() => setView('list')}
                aria-label="Listevisning"
              >
                ≡
              </ActionIcon>
            </Tooltip>
            <Button onClick={() => navigate('/lost-claims/new')}>+ Ny efterlysning</Button>
          </Group>
        </Group>

        <Stack gap="xs">
          <TextInput
            placeholder="Søg beskrivelse, navn, mærke..."
            value={search}
            onChange={(e) => { setSearch(e.currentTarget.value); clearSelection(); }}
          />
          <SegmentedControl
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v as ClaimStatus | 'all'); clearSelection(); }}
            data={[
              { label: 'Åbne', value: 'open' },
              { label: 'Matchede', value: 'matched' },
              { label: 'Lukkede', value: 'closed' },
              { label: 'Alle', value: 'all' },
            ]}
            size="xs"
          />
        </Stack>

        {isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
        {error && <Text c="red">Fejl: {(error as Error).message}</Text>}

        {!isLoading && !error && displayed.length === 0 && (
          <Center py="xl">
            <Text c="dimmed">Ingen efterlysninger.</Text>
          </Center>
        )}

        {displayed.length > 0 && (
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={() => toggleAll(displayed.map((c) => c.id))}
            label={allSelected ? 'Fravælg alle' : 'Vælg alle'}
            size="sm"
          />
        )}

        {displayed.length > 0 && view === 'grid' && (
          <SimpleGrid cols={{ base: 1, xs: 2, sm: 3 }} spacing="md">
            {displayed.map((claim) => (
              <ClaimCard
                key={claim.id}
                claim={claim}
                selected={selectedIds.has(claim.id)}
                onToggle={toggleItem}
              />
            ))}
          </SimpleGrid>
        )}

        {displayed.length > 0 && view === 'list' && (
          <Stack gap={0}>
            {displayed.map((claim) => (
              <ClaimRow
                key={claim.id}
                claim={claim}
                selected={selectedIds.has(claim.id)}
                onToggle={toggleItem}
                onUpdate={(id, status) => updateMutation.mutate({ id, status })}
                updating={updateMutation.isPending}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {someSelected && (
        <Paper
          withBorder
          shadow="md"
          p="md"
          style={{ position: 'sticky', bottom: 16, marginTop: 16 }}
        >
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {selectedIds.size} efterlysning{selectedIds.size !== 1 ? 'er' : ''} valgt
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="subtle" color="gray" onClick={() => clearSelection()}>
                Fravælg
              </Button>
              <Button
                size="xs"
                color="red"
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate([...selectedIds])}
              >
                Slet {selectedIds.size} efterlysning{selectedIds.size !== 1 ? 'er' : ''}
              </Button>
            </Group>
          </Group>
        </Paper>
      )}
    </div>
  );
}
